/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Else, If, Then, When } from "react-if";
import { Text, theme } from "@nordcloud/gnui";
import {
  SearchSortField,
  SearchSortOrder,
  SearchV2Filter,
} from "~/generated/graphql";
import {
  DateRange,
  ErrorWrap,
  Granularity,
  LoaderWrap,
  Pagination,
  ReactTable,
  SortDirection,
} from "~/components";
import { ERROR_COLORS, EstatePeriod } from "~/constants";
import {
  useCurrency,
  useEstateRecordsV2,
  useEstateSortV2,
  useFilteredQueryState,
  estateSearchJobFields,
} from "~/hooks";
import { FiltersProp } from "~/models/estateRecordRow";
import { useCustomers } from "~/services/customers";
import { showError } from "~/services/toast";
import { isEmpty, isNotEmpty } from "~/tools";
import {
  EstateRecordsLoader,
  ParametersBar,
  EstateRecordsAccumulatedCostChart,
  useEstateChartParameters,
} from "./components";
import { filterWasteFilter } from "./components/helpers";
import { EstateListColumns } from "./EstateList";
import { convertTags, generateNewRowData, getMultiResources } from "./helpers";

type Props = {
  period: EstatePeriod;
  range: DateRange;
  granularity: Granularity;
  setIsDataLoading: (loading: boolean) => void;
};

type SortingProp = {
  field?: SearchSortField;
  order?: SearchSortOrder;
};

type EstateQueryState = FiltersProp &
  SearchV2Filter &
  SortingProp & {
    page: number;
    limit: number;
    query: string;
  };

export function EstateRecordsAccumulatedView({
  period,
  range,
  granularity,
  setIsDataLoading,
}: Props) {
  const { search, setSearch } = useEstateChartParameters();
  const { currency } = useCurrency();
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [isChartDataLoading, setIsChartDataLoading] = useState(false);
  const { state, updateQueryState } = useFilteredQueryState(
    estateSearchJobFields
  );

  const { isAdminLite } = useCustomers();

  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const { page, limit, query, field, order, ...restFilters }: EstateQueryState =
    state;

  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    useEstateSortV2({ field, order, updateQueryState });

  const isEstateRecordsVisible = period !== EstatePeriod.RANGE;

  const estateFilters = {
    filter: {
      ...filterWasteFilter(restFilters),
      metadata: convertTags(restFilters.metadata),
      tags: convertTags(restFilters.tags),
    },
    query,
    sort: {
      field: sortField,
      order: sortDirection,
    },
  };

  const { data, loading, error, refetch } = useEstateRecordsV2(
    {
      variables: {
        page,
        limit,
        ...estateFilters,
      },
      skip: !isEstateRecordsVisible,
    },
    showError,
    range.from
  );

  const rows = useMemo(
    () =>
      data?.resources?.map((res) => ({
        ...generateNewRowData(res, currency),
      })) ?? [],
    [data, currency]
  );

  const hasResources = isNotEmpty(rows);
  const estateCount = data?.count ?? 0;
  const showPagination = estateCount > 0;

  const columns = useMemo(
    () => EstateListColumns({ isLite: isAdminLite }),
    [isAdminLite]
  );

  const resources = useMemo(
    () => getMultiResources(selectedResources, data?.resources),
    [selectedResources, data?.resources]
  );

  const selectedRowsHandler = useCallback(
    (items: string[]) => {
      setSelectedResources(
        items.filter(
          (item) =>
            data?.resources
              ?.filter((row) => row.__typename !== "CostResource")
              .map((row) => row.id)
              .includes(item)
        )
      );
    },
    [setSelectedResources, data?.resources]
  );

  useEffect(() => {
    setIsDataLoading(loading || isChartDataLoading);
  }, [loading, isChartDataLoading, setIsDataLoading]);

  const sortOrder =
    sortDirection === SearchSortOrder.Asc
      ? SortDirection.Ascending
      : SortDirection.Descending;

  return (
    <div>
      <EstateRecordsAccumulatedCostChart
        granularity={granularity}
        period={period}
        range={range}
        isCostLoading={loading}
        setIsChartDataLoading={setIsChartDataLoading}
        currentMonthTotal={data?.cost?.currentMonthTotal}
        monthlySavings={data?.savingsAmount?.total ?? ""}
        estateRecordsCount={data?.count ?? 0}
        costForecast={data?.cost?.monthlyForecast ?? ""}
      />
      <When condition={isEstateRecordsVisible}>
        <ParametersBar
          search={search}
          setSearch={setSearch}
          disabledAttachment={isEmpty(selectedResources)}
          sortField={sortField}
          resources={resources}
          refresh={refetch}
          sortDirection={sortOrder}
          totalRecords={data?.count ?? 0}
          queryFilters={estateFilters}
          disabled={loading || isChartDataLoading}
          onSortOrderChange={onSortOrderChange}
          onSortSelect={onSortSelect}
        />
        <ErrorWrap
          error={error}
          status={
            hasResources ? ERROR_COLORS.notification : ERROR_COLORS.danger
          }
        >
          <LoaderWrap loading={loading} Component={EstateRecordsLoader}>
            <If condition={hasResources}>
              <Then>
                <ReactTable
                  columns={columns}
                  data={rows}
                  sort={false}
                  headerBackground={theme.color.background.ui03}
                  deselectRows={isEmpty(selectedResources ?? [])}
                  onSelectedRowsChange={selectedRowsHandler}
                />
                <When condition={showPagination}>
                  <Pagination count={estateCount} />
                </When>
              </Then>
              <Else>
                <Text
                  paddingTop={theme.spacing.spacing04}
                  paddingBottom={theme.spacing.spacing04}
                >
                  No resources matching selected criteria
                </Text>
              </Else>
            </If>
          </LoaderWrap>
        </ErrorWrap>
      </When>
    </div>
  );
}
